import { LocationAPI, SiteAPI } from "@/lib/client/location-api";
import {
  CreateLocationRequest,
  CreateSiteRequest,
  Location,
  LocationsGroupedResponse,
  LocationTypeOption,
  Site,
  SitesGroupedResponse,
  SiteTypeOption,
  UpdateLocationRequest,
  UpdateSiteRequest,
} from "@/types/location";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useDelegation } from "./use-delegation";

// Query keys for Sites (primary)
export const siteKeys = {
  all: ["sites"] as const,
  lists: () => [...siteKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...siteKeys.lists(), { filters }] as const,
  details: () => [...siteKeys.all, "detail"] as const,
  detail: (id: string) => [...siteKeys.details(), id] as const,
  types: () => [...siteKeys.all, "types"] as const,
};

// Query keys for Locations (backwards compatible, shares cache with sites)
export const locationKeys = {
  all: ["sites"] as const, // Use same cache as sites
  lists: () => [...locationKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...locationKeys.lists(), { filters }] as const,
  details: () => [...locationKeys.all, "detail"] as const,
  detail: (id: string) => [...locationKeys.details(), id] as const,
  types: () => [...locationKeys.all, "types"] as const,
};

// Hook to get all locations grouped by type
export function useLocations() {
  const { isDelegating, currentDelegation } = useDelegation();
  
  // Include delegation context in query key to ensure proper cache separation
  const queryKey = isDelegating && currentDelegation
    ? [...locationKeys.lists(), { delegationType: currentDelegation.type, delegationId: currentDelegation.entityId }]
    : locationKeys.lists();

  return useQuery({
    queryKey,
    queryFn: LocationAPI.getLocations,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to get location types
export function useLocationTypes() {
  return useQuery({
    queryKey: locationKeys.types(),
    queryFn: LocationAPI.getLocationTypes,
    staleTime: 30 * 60 * 1000, // 30 minutes - types don't change often
  });
}

// Hook to get specific location
export function useLocation(id: string) {
  return useQuery({
    queryKey: locationKeys.detail(id),
    queryFn: () => LocationAPI.getLocation(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to get locations summary
export function useLocationsSummary() {
  const { isDelegating, currentDelegation } = useDelegation();
  
  // Include delegation context in query key
  const queryKey = isDelegating && currentDelegation
    ? [...locationKeys.lists(), "summary", { delegationType: currentDelegation.type, delegationId: currentDelegation.entityId }]
    : [...locationKeys.lists(), "summary"];

  return useQuery({
    queryKey,
    queryFn: LocationAPI.getLocationsSummary,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook to create location
export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLocationRequest) =>
      LocationAPI.createLocation(data),
    onSuccess: (newLocation) => {
      // Invalidate and refetch locations
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });

      // Add the new location to the cache
      queryClient.setQueryData(
        locationKeys.detail(newLocation.id),
        newLocation
      );

      toast.success(
        `${newLocation.locationType === 'location' ? 'Location' : newLocation.locationType} "${newLocation.name}" created successfully`
      );
    },
    onError: (error: any) => {
      toast.error(`Failed to create location: ${error.message}`);
    },
  });
}

// Hook to update location
export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateLocationRequest;
    }) => LocationAPI.updateLocation(id, data),
    onSuccess: (updatedLocation) => {
      // Update the specific location in cache
      queryClient.setQueryData(
        locationKeys.detail(updatedLocation.id),
        updatedLocation
      );

      // Invalidate lists to refresh grouping (in case location type changed)
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });

      toast.success(
        `${updatedLocation.locationType === 'location' ? 'Location' : updatedLocation.locationType} "${updatedLocation.name}" updated successfully`
      );
    },
    onError: (error: any) => {
      toast.error(`Failed to update location: ${error.message}`);
    },
  });
}

// Hook to delete location
export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => LocationAPI.deleteLocation(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: locationKeys.detail(deletedId),
      });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });

      toast.success("Location deleted successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to delete location: ${error.message}`);
    },
  });
}

// =============================================================================
// Site hooks (primary - use these for new code)
// =============================================================================

// Hook to get all sites grouped by type
export function useSites() {
  const { isDelegating, currentDelegation } = useDelegation();

  // Include delegation context in query key to ensure proper cache separation
  const queryKey = isDelegating && currentDelegation
    ? [...siteKeys.lists(), { delegationType: currentDelegation.type, delegationId: currentDelegation.entityId }]
    : siteKeys.lists();

  return useQuery({
    queryKey,
    queryFn: SiteAPI.getSites,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to get site types
export function useSiteTypes() {
  return useQuery({
    queryKey: siteKeys.types(),
    queryFn: SiteAPI.getSiteTypes,
    staleTime: 30 * 60 * 1000, // 30 minutes - types don't change often
  });
}

// Hook to get specific site
export function useSite(id: string) {
  return useQuery({
    queryKey: siteKeys.detail(id),
    queryFn: () => SiteAPI.getSite(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to get sites summary
export function useSitesSummary() {
  const { isDelegating, currentDelegation } = useDelegation();

  // Include delegation context in query key
  const queryKey = isDelegating && currentDelegation
    ? [...siteKeys.lists(), "summary", { delegationType: currentDelegation.type, delegationId: currentDelegation.entityId }]
    : [...siteKeys.lists(), "summary"];

  return useQuery({
    queryKey,
    queryFn: SiteAPI.getSitesSummary,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook to create site
export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSiteRequest) => SiteAPI.createSite(data),
    onSuccess: (newSite) => {
      // Invalidate and refetch sites
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });

      // Add the new site to the cache
      queryClient.setQueryData(siteKeys.detail(newSite.id), newSite);

      const typeLabel = newSite.siteType === 'site' ? 'Site' : newSite.siteType.charAt(0).toUpperCase() + newSite.siteType.slice(1);
      toast.success(`${typeLabel} "${newSite.name}" created successfully`);
    },
    onError: (error: any) => {
      toast.error(`Failed to create site: ${error.message}`);
    },
  });
}

// Hook to update site
export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSiteRequest }) =>
      SiteAPI.updateSite(id, data),
    onSuccess: (updatedSite) => {
      // Update the specific site in cache
      queryClient.setQueryData(siteKeys.detail(updatedSite.id), updatedSite);

      // Invalidate lists to refresh grouping (in case site type changed)
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });

      const typeLabel = updatedSite.siteType === 'site' ? 'Site' : updatedSite.siteType.charAt(0).toUpperCase() + updatedSite.siteType.slice(1);
      toast.success(`${typeLabel} "${updatedSite.name}" updated successfully`);
    },
    onError: (error: any) => {
      toast.error(`Failed to update site: ${error.message}`);
    },
  });
}

// Hook to delete site
export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => SiteAPI.deleteSite(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: siteKeys.detail(deletedId) });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });

      toast.success("Site deleted successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to delete site: ${error.message}`);
    },
  });
}